import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Timer, Save, Loader2 } from "lucide-react";
import api from "../../lib/api";

const FIELDS = [
  {
    key: "tier1_reminder_hours_before_due",
    label: "Tier 1 — Reminder before due",
    unit: "hours before due",
    hint: "Assignee gets a nudge notification + email this many hours before the task's due_at.",
  },
  {
    key: "tier2_manager_hours_overdue",
    label: "Tier 2 — Escalate to manager",
    unit: "hours after due",
    hint: "If the task is still Open after this many hours past due_at, it hands off to a manager.",
  },
  {
    key: "tier3_super_admin_hours_overdue",
    label: "Tier 3 — Escalate to super admin",
    unit: "hours after due",
    hint: "Final escalation — if still open after this many hours, the file goes to a super admin.",
  },
];

export default function SlaLadderTab() {
  const [ladder, setLadder] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/admin/settings/sla-ladder");
      setLadder(res.data.ladder);
      setDefaults(res.data.defaults);
    } catch (e) { toast.error("Couldn't load SLA ladder"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/sla-ladder", ladder);
      toast.success("SLA ladder saved.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = () => setLadder({ ...defaults });

  if (!ladder) return <div className="text-sm text-ink-muted">Loading…</div>;

  return (
    <div className="border border-navy/10 rounded-lg bg-white p-6 max-w-3xl" data-testid="sla-ladder-tab">
      <div className="flex items-center gap-2 mb-1">
        <Timer size={14} className="text-emerald-700" />
        <div className="overline text-emerald-700">SLA Escalation Ladder</div>
      </div>
      <div className="text-xs text-ink-muted mb-6">
        Configure how the SLA cron reacts as tasks approach and pass their due date.
        Values must be 0–720 hours (30 days).
      </div>

      <div className="space-y-5">
        {FIELDS.map((f) => (
          <div key={f.key} data-testid={`sla-tier-${f.key}`}>
            <div className="flex items-center justify-between mb-1">
              <label className="font-heading text-sm text-ink">{f.label}</label>
              <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                default {defaults?.[f.key]}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={720}
                value={ladder[f.key] ?? ""}
                onChange={(e) => setLadder({ ...ladder, [f.key]: Number(e.target.value) })}
                className="w-24 px-3 py-2 border border-navy/15 rounded-md text-sm text-ink focus:outline-none focus:ring-1 focus:ring-emerald-400"
                data-testid={`sla-input-${f.key}`}
              />
              <span className="text-xs text-ink-muted">{f.unit}</span>
            </div>
            <div className="text-[11px] text-ink-muted mt-1">{f.hint}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-center gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-heading inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="sla-save"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save ladder"}
        </button>
        <button
          onClick={reset}
          className="px-4 py-2 rounded-md border border-navy/15 text-ink text-sm hover:bg-navy/[0.04]"
          data-testid="sla-reset"
        >
          Reset to defaults
        </button>
      </div>
    </div>
  );
}
